import {
  fetchEnvVariables as fetchEnvVariablesNetwork,
  getFetchEnvVariablesQueryKey,
} from '@unifyapps/network/generated/workflow-builder-rest-api/workflow-builder-rest-api';
import type {
  QueryClient,
  QueryFunctionContext,
  UseQueryOptions,
} from '@unifyapps/network/react-query';
import { useQuery } from '@unifyapps/network/react-query';
import { useUserStore } from '../auth/UserStoreProvider';
import { EMPTY_ENV_VARIABLES } from '../consts/envVars';

export type EnvVariables = Record<string, string>;

const QUERY_KEY = getFetchEnvVariablesQueryKey();

// `/api/workflow-builder/env-variables` is intentionally not exposed in some
// deployments — it returns 404, and `EMPTY_ENV_VARIABLES` is the correct
// fallback for that case. Other failures (5xx, network, parse) indicate a
// real problem and are rethrown so observability can pick them up.
// forward React Query's `meta` (carries apiDomain via QueryProvider default options) and `signal`
// to the request. Without meta the call has no apiDomain, so in delta-matrix it resolves against the
// embedding page's origin instead of the configured host — sending it cross-domain and tripping CORS.
async function safeFetchEnvVariables({ meta, signal }: QueryFunctionContext): Promise<EnvVariables> {
  try {
    const data = (await fetchEnvVariablesNetwork({ meta }, signal)) as unknown as
      | EnvVariables
      | undefined;
    return data ?? EMPTY_ENV_VARIABLES;
  } catch (err) {
    if ((err as { status?: number } | null)?.status === 404) {
      return EMPTY_ENV_VARIABLES;
    }
    throw err;
  }
}

const SAFE_QUERY_DEFAULTS = {
  queryKey: QUERY_KEY,
  queryFn: safeFetchEnvVariables,
  retry: false,
  staleTime: Infinity,
} as const;

export async function fetchEnvVariables(queryClient: QueryClient) {
  await queryClient.prefetchQuery(SAFE_QUERY_DEFAULTS);
  const cached = queryClient.getQueryData<EnvVariables>(QUERY_KEY);
  return cached ?? EMPTY_ENV_VARIABLES;
}

export async function prefetchEnvVariables(queryClient: QueryClient) {
  await queryClient.prefetchQuery(SAFE_QUERY_DEFAULTS);
  return queryClient;
}

export function useFetchEnvVariables<TData = EnvVariables>(options?: {
  query?: Partial<UseQueryOptions<EnvVariables, Error, TData>>;
}) {
  return useQuery<EnvVariables, Error, TData>({
    ...SAFE_QUERY_DEFAULTS,
    ...options?.query,
  });
}

export const useEnvVariables = () => {
  const userDetails = useUserStore().hooks.useCurrentUserDetails();

  /**
   * While updating this check for enabled, make sure to update the isPending check below
   *
   * Q: What all should be added in the isPending check?
   * A: Anything that makes the enabled boolean false because of something being undefined right now and later can become true.
   *    i.e., any variable check whose value is async.
   *    isPending should be true until the point that async variable is resolved
   */
  const enabled = Boolean(userDetails?.user?.id && !userDetails?.external);

  const {
    data: envVariables,
    isLoading,
    error,
  } = useFetchEnvVariables<EnvVariables>({
    query: {
      enabled,
      staleTime: Infinity,
    },
  });

  return {
    envVariables: (envVariables ?? EMPTY_ENV_VARIABLES) as EnvVariables,
    isLoading,
    error,
    /**
     * isPending is exposed to fix a flicker at places where a fallback is shown based on isLoading
     *
     * Case 1: user id is present in userDetails and if userDetails?.external is false
     *    -> when initially userDetails are not present, enabled is false and because of that isLoading will be false
     *    -> when userDetails become available, enabled will become true and because of that query will be triggered and isLoading will be true
     *    -> Due to this, when isLoading is used as a blocking condition to render some fallback or loading state, it will lead to a flicker
     *
     * hence we will be making isLoading true even when userDetails are not present as enabled is not yet decided.
     *
     * Case 2: user id is present in userDetails and if userDetails?.external is true
     *    -> when initially userDetails are not present, enabled is false and because of that isLoading will be false but !userDetails will be true
     *    -> when userDetails become available, enabled will become false and since no query will be triggered, isLoading will be false
     */
    isPending: !userDetails,
  };
};
